import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  ReactNode,
} from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { permissionIdentity } from "./permission-identity";
import { hasApplicationGrant } from "./application-access";

interface PermissionsContextValue {
  /** All permission keys for the current user, indexed for O(1) lookup */
  permissions: Set<string>;
  attributes: Set<string>;
  /**
   * Check whether the user has a specific permission. Returns true
   * unconditionally for SuperAdmins (the backend signals this via the
   * is_super_admin flag, not via the permission set).
   */
  has: (application: string, feature: string, category: string) => boolean;
  hasAttribute: (application: string, feature: string, attribute: string) => boolean;
  /** Whether the effective permission set contains any grant for an application. */
  hasApplication: (application: string) => boolean;
  /** True when the backend reported this user as a SuperAdmin / master-role holder */
  isSuperAdmin: boolean;
  /** Re-fetch permissions (call after admin mutates role mappings) */
  refresh: () => Promise<void>;
  /** True while the first fetch is in flight */
  loading: boolean;
  /** Last error message from the fetch, if any */
  error: string | null;
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

interface PermissionsProviderProps {
  children: ReactNode;
}

export function PermissionsProvider({ children }: PermissionsProviderProps) {
  const tenantName = useSelector((s: RootState) => s.auth.tenantName);
  const token = useSelector((s: RootState) => s.auth.token);
  const tenantNameRef = useRef(tenantName);
  const tokenRef = useRef(token);
  tenantNameRef.current = tenantName;
  tokenRef.current = token;
  const requestVersion = useRef(0);
  const [loadedIdentity, setLoadedIdentity] = useState<string | null>(null);
  const identity = permissionIdentity(tenantName, token);

  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [attributes, setAttributes] = useState<Set<string>>(new Set());
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (showInitialLoading = false) => {
    const version = ++requestVersion.current;
    if (!identity) {
      setLoadedIdentity(null);
      setPermissions(new Set());
      setAttributes(new Set());
      setIsSuperAdmin(false);
      setLoading(false);
      setError(null);
      return;
    }
    if (showInitialLoading) {
      // Clear grants only when the authenticated identity changes. Access-token
      // rotation for the same identity must not blank the application or
      // remount protected routes.
      setLoadedIdentity(null);
      setPermissions(new Set());
      setAttributes(new Set());
      setIsSuperAdmin(false);
      setLoading(true);
    }
    setError(null);
    try {
      // Do not use the shared IDP service here. Its module-level auth state can
      // survive a logout/login switch and send the previous user's token. The
      // Redux token is the source of truth for the identity this provider is
      // rendering, so bind every permission request to that exact token.
      const response = await fetch(
        `/idp/api/tenants/${encodeURIComponent(tenantNameRef.current!)}/me/permissions`,
        {
          headers: {
            Authorization: `Bearer ${tokenRef.current}`,
            "Cache-Control": "no-cache, no-store",
            Pragma: "no-cache",
          },
          cache: "no-store",
        },
      );
      if (!response.ok) {
        throw new Error(`Permission request failed with status ${response.status}`);
      }
      const res = await response.json();
      if (version !== requestVersion.current) return;
      const list: string[] = res?.data?.permissions ?? [];
      const attributeList: string[] = res?.data?.attributes ?? [];
      const superAdmin: boolean = !!res?.data?.is_super_admin;
      setPermissions(new Set(list));
      setAttributes(new Set(attributeList));
      setIsSuperAdmin(superAdmin);
      setLoadedIdentity(identity);
    } catch (err: any) {
      if (version !== requestVersion.current) return;
      console.error("Failed to load permissions", err);
      setError(err?.message || "Failed to load permissions");
      setPermissions(new Set());
      setAttributes(new Set());
      setIsSuperAdmin(false);
    } finally {
      if (version === requestVersion.current && showInitialLoading) {
        setLoading(false);
      }
    }
  }, [identity]);

  useEffect(() => {
    // `identity` is stable across access-token rotation. This effect therefore
    // runs on first load, logout, or a real user/tenant switch, not every time
    // a five-minute access token is renewed.
    load(true);
    return () => { requestVersion.current += 1; };
  }, [load]);

  const refresh = useCallback(() => load(false), [load]);

  const ready = identity !== null && loadedIdentity === identity && !loading;

  const value = useMemo<PermissionsContextValue>(
    () => ({
      permissions: ready ? permissions : new Set<string>(),
      attributes: ready ? attributes : new Set<string>(),
      isSuperAdmin: ready && isSuperAdmin,
      // SuperAdmin short-circuit lives here so every consumer (hook, gate,
      // route guard) gets it for free without re-checking the flag itself.
      has: (application: string, feature: string, category: string) =>
        ready && (isSuperAdmin ||
        permissions.has(`${application}:${feature}:${category}`)),
      hasAttribute: (application: string, feature: string, attribute: string) =>
        ready && (isSuperAdmin ||
        attributes.has(`${application}:${feature}:${attribute}`)),
      hasApplication: (application: string) => {
        if (!ready) return false;
        if (isSuperAdmin) return true;
        return hasApplicationGrant(application, permissions, attributes);
      },
      refresh,
      loading,
      error,
    }),
    [permissions, attributes, isSuperAdmin, refresh, loading, error, ready],
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissionsContext(): PermissionsContextValue {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error(
      "usePermissionsContext must be used inside a <PermissionsProvider>",
    );
  }
  return ctx;
}
